import { DateTime } from 'luxon'
import { useCallback, useEffect, useState } from 'react'
import { ButtonGroup, Col, Form, Modal, Row, ToggleButton } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import CreatableSelect from 'react-select/creatable'
import styled from 'styled-components'

import { codeItemAdd, codeListGet, tagManagerPersist } from '../../../api'
import { AttachmentView } from '../../../common/attachments/attachmentview'
import { Button } from '../../../common/Button.tsx'
import DefaultDatePicker from '../../../common/datepicker/DefaultDatePicker.jsx'
import { PersonSelect } from '../../../common/personselect'
import { creatableSelectCodeSettings, creatableSelectTagSettings } from '../../../common/reactselect'
import Switch from '../../../common/Switch.jsx'
import { fetchTags } from '../../../common/utils'
import { actionTypeSelector, authSelector } from '../../../reducers'

// styled toggle button with primary color
const StyledToggleButton = styled(ToggleButton)`
  &.btn-primary {
    background-color: #5952E0 !important;
    border-color: #5952E0 !important;
    color: #ffffff !important;

    &:hover {
      background-color: #3C3894 !important;
      border-color: #3C3894 !important;
    }
}
    &.btn-outline-primary {
      background-color: #ffffff !important;
      border-color: #5952E0 !important;
      color:  #5952E0 !important;

    &:hover {
      background-color: #5952E00A !important;
      border-color: #3C3894 !important;
    }
  }
`

// code lists
const courseCodes = 'competenceCourses'

function ToggleButtonTrainerType({ data, setData, t }) {
	const radios = [
		{ name: t('Internal', 'Sisäinen'), value: 'INTERNAL' },
		{ name: t('External', 'Ulkoinen'), value: 'EXTERNAL' },
	]

	const handleRadioChange = ({ target }) => {
		setData({ ...data, [target.name]: target.value })
	}

	return (
		<ButtonGroup className="mb-2">
			{radios.map((radio, idx) => (
				<StyledToggleButton
					key={radio.value}
					id={`trainerTypeRadio-${idx}`}
					name="trainerType"
					type="radio"
					value={radio.value}
					checked={data?.trainerType === radio.value}
					variant={data?.trainerType === radio.value ? 'primary' : 'outline-primary'}
					onChange={handleRadioChange}
				>
					{radio.name}
				</StyledToggleButton>
			))}
		</ButtonGroup>
	)
}

export function AddCourse({ show, setShow, courseData, saveData }) {
	const { t } = useTranslation()
	const actionType = useSelector(actionTypeSelector)
	const auth = useSelector(authSelector)
	const [data, setData] = useState(null)
	const prefix = 'courseModal'
	const path = window.location.pathname
	const isPersonal = path.startsWith('/personal')

	// react-select
	const handleCodeItem = (newValue, actionMeta) =>
		setData({ ...data, [actionMeta.name]: { codeItemId: newValue.value } })

	const [courseOptions, setCourseOptions] = useState([])

	const fetchCourses = useCallback(async () => {
		const res = await codeListGet(courseCodes)
		setCourseOptions(
			res.data.items
				? res.data.items
						.map((item) => ({
							value: item.codeItemId, // item.key
							label: item.codeValue, // item.description
						}))
						.sort((a, b) => a.label.localeCompare(b.label))
				: [],
		)
	}, [])

	const handleCreateCourse = async (value) => {
		const res = await codeItemAdd(courseCodes, { codeValue: value })
		const { codeItemId, codeValue } = res.data
		setCourseOptions([...courseOptions, { value: codeItemId, label: codeValue }])
		setData({
			...data,
			courseCodeItem: { codeItemId: codeItemId },
		})
	}

	const [tagOptions, setTagOptions] = useState([])

	const handleTags = (options) =>
		setData({
			...data,
			tags: options.map((option) => ({ codeItemId: option.value, codeKey: option.label })),
		})

	const handleCreateTag = async (value) => {
		const res = await tagManagerPersist([{ codeKey: value }])
		const { codeItemId, codeKey } = res.data[0]
		setTagOptions([...tagOptions, { value: codeItemId, label: codeKey }])
		const currentTags = data.tags ?? []
		setData({
			...data,
			tags: [...currentTags, { codeItemId: codeItemId, codeKey: codeKey }],
		})
	}

	// fetch code lists
	useEffect(() => {
		fetchCourses()
		fetchTags(setTagOptions)
	}, [fetchCourses])

	useEffect(() => setData(courseData), [courseData])

	const dataChange = ({ target }) => {
		const newData = { ...data }
		const value = target.type === 'checkbox' ? target.checked : target.value
		newData[target.id] = value
		setData(newData)
	}

	const handleDateTimeChange =
		(field, part = 'date') =>
		(isoOrEvent) => {
			const newData = { ...data }
			const newValue = part === 'date' ? (isoOrEvent ?? '') : isoOrEvent.target.value
			const existing = data?.[field] ?? ''
			const [existingDate = '', existingTime = '00:00'] = existing.split('T')

			const date = part === 'date' ? newValue : existingDate
			const time = part === 'time' ? newValue : existingTime

			if (!date) {
				newData[field] = ''
				setData(newData)
				return
			}

			const newDateTime = `${date}T${time || '00:00'}`
			newData[field] = newDateTime

			const otherField = field === 'startDateTime' ? 'endDateTime' : 'startDateTime'
			if (data[otherField] && time && time !== '00:00') {
				const end = field === 'startDateTime' ? DateTime.fromISO(data[otherField]) : DateTime.fromISO(newDateTime)
				const start = field === 'startDateTime' ? DateTime.fromISO(newDateTime) : DateTime.fromISO(data[otherField])
				const diff = end.diff(start, ['hours', 'days', 'months', 'years'])
				newData.durationHours = diff.hours
				newData.durationDays = diff.days
				newData.durationMonths = diff.months
				newData.durationYears = diff.years
			}

			setData(newData)
		}

	// react-select
	const selectChange = (newValue, actionMeta) => setData({ ...data, [actionMeta.name]: newValue.value })

	const handleSave = () => saveData(data)
	const handleClose = () => setShow(false)

	let title
	if (data?.courseId > 0) {
		title = data?.courseCodeItem?.codeValue ?? t('OtherEducation', 'Muu koulutus')
	} else {
		title = t('AddOtherEducation', 'Lisää muu koulutus')
	}

	return (
		show &&
		data && (
			<Modal show onHide={handleClose} backdrop="static" size="xl" fullscreen="lg-down" scrollable>
				<Modal.Header closeButton>
					<Modal.Title>{title}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Row className="mb-1">
						<PersonSelect
							disabled={data?.courseId > 0 || isPersonal}
							selectedPerson={data}
							selectChange={selectChange}
							supervised={auth.role === 'SUPERVISOR'}
						/>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="courseCodeItem">
							<Form.Label>{t('NameofCourse', 'Koulutuksen/kurssin nimi')}</Form.Label>
							<CreatableSelect
								name="courseCodeItem"
								inputId="courseCodeItem"
								value={courseOptions.find((option) => option.value === data?.courseCodeItem?.codeItemId)}
								onChange={handleCodeItem}
								onCreateOption={handleCreateCourse}
								isValidNewOption={(inputValue) => inputValue.length > 0 && auth.knowledgeAdmin}
								options={courseOptions}
								{...creatableSelectCodeSettings(t)}
							/>
						</Form.Group>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="trainerOrganizer">
							<Form.Label>{t('ArrangedBy', 'Järjestävä organisaatio')}</Form.Label>
							<Form.Control onChange={dataChange} value={data?.trainerOrganizer ?? ''} />
						</Form.Group>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="trainerName">
							<Form.Label>{t('TrainerName', 'Kouluttajan nimi')}</Form.Label>
							<Form.Control
								placeholder={t('Trainer', 'Kouluttaja')}
								onChange={dataChange}
								value={data?.trainerName ?? ''}
							/>
						</Form.Group>
						<Form.Group as={Col} controlId="trainertype" style={{ marginTop: '28px' }}>
							<ToggleButtonTrainerType data={data} setData={setData} t={t} />
						</Form.Group>
					</Row>

					<Row className="mb-1 g-4">
						<Col className="d-flex gap-2 align-items-end">
							<div className="flex-grow-1">
								<DefaultDatePicker
									id={`${prefix}-startDateTime`}
									name="startDateTime"
									label={t('Start', 'Aloitus')}
									value={data?.startDateTime?.split('T')[0] ?? ''}
									onChange={handleDateTimeChange('startDateTime')}
								/>
							</div>
							<div className="flex-grow-1">
								<Form.Label>{t('Time', 'Aika')}</Form.Label>
								<Form.Control
									id="startTime"
									type="time"
									lang="fi"
									onChange={handleDateTimeChange('startDateTime', 'time')}
									value={data?.startDateTime?.split('T')[1]?.slice(0, 5) ?? ''}
								/>
							</div>
						</Col>
						<Col className="d-flex gap-2 align-items-end">
							<div className="flex-grow-1">
								<DefaultDatePicker
									id={`${prefix}-endDateTime`}
									name="endDateTime"
									label={t('End', 'Lopetus')}
									value={data?.endDateTime?.split('T')[0] ?? ''}
									onChange={handleDateTimeChange('endDateTime')}
								/>
							</div>
							<div className="flex-grow-1">
								<Form.Label>{t('Time', 'Aika')}</Form.Label>
								<Form.Control
									id="endTime"
									type="time"
									lang="fi"
									onChange={handleDateTimeChange('endDateTime', 'time')}
									value={data?.endDateTime?.split('T')[1]?.slice(0, 5) ?? ''}
								/>
							</div>
						</Col>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="durationprompt">
							<Form.Label>{t('Duration', 'Kesto')}</Form.Label>
						</Form.Group>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="durationHours" className="pr-0">
							<Form.Control type="number" min={0} max={23} onChange={dataChange} value={data?.durationHours ?? ''} />
						</Form.Group>
						<Form.Group as={Col} controlId="durationHours" className="p-0">
							<Form.Label>{t('Hours', 'Tuntia')}</Form.Label>
						</Form.Group>

						<Form.Group as={Col} controlId="durationDays" className="p-0">
							<Form.Control type="number" min={0} max={31} onChange={dataChange} value={data?.durationDays ?? ''} />
						</Form.Group>
						<Form.Group as={Col} controlId="durationDays" className="p-0">
							<Form.Label>{t('Days', 'Päivää')}</Form.Label>
						</Form.Group>

						<Form.Group as={Col} controlId="durationMonths" className="p-0">
							<Form.Control type="number" min={0} max={12} onChange={dataChange} value={data?.durationMonths ?? ''} />
						</Form.Group>
						<Form.Group as={Col} controlId="durationMonths" className="p-0">
							<Form.Label>{t('Months', 'Kuukautta')}</Form.Label>
						</Form.Group>

						<Form.Group as={Col} controlId="durationYears" className="p-0">
							<Form.Control type="number" min={0} onChange={dataChange} value={data?.durationYears ?? ''} />
						</Form.Group>
						<Form.Group as={Col} controlId="durationYears" className="p-0">
							<Form.Label>{t('Years', 'Vuotta')}</Form.Label>
						</Form.Group>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="educationContent">
							<Form.Label>{t('CourseContent', 'Koulutuksen sisältö')}</Form.Label>
							<Form.Control as="textarea" rows={3} onChange={dataChange} value={data?.educationContent ?? ''} />
						</Form.Group>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="certificationName">
							<Form.Label>{t('LicenseCertificateCard', 'Lisenssi/sertifikaatio/kortti')}</Form.Label>
							<Form.Control required onChange={dataChange} value={data?.certificationName ?? ''} />
						</Form.Group>

						<Form.Group as={Col} controlId="certificationNoExpiration" style={{ marginTop: '2rem' }}>
							<Switch
								className="mb-1"
								id="certificationNoExpiration"
								label={t('DoesNotExpire', 'Ei vanhene')}
								onChange={dataChange}
								checked={data?.certificationNoExpiration ?? ''}
							/>
						</Form.Group>
					</Row>

					{!data?.certificationNoExpiration && (
						<Row className="mb-1">
							<Form.Group as={Col} xl={4} className="mt-4" controlId="certificationValidUntil">
								<DefaultDatePicker
									id={`${prefix}-certificationValidUntil`}
									name="certificationValidUntil"
									label={t('CourseValidThru', 'Voimassaolon päät.pvm')}
									required
									value={data?.certificationValidUntil ?? ''}
									onChange={(iso) => setData({ ...data, certificationValidUntil: iso })}
								/>
							</Form.Group>

							<Form.Group as={Col} xl={4} controlId="certificationReminder" style={{ marginTop: '2rem' }}>
								<Switch
									className="mb-1"
									id="certificationReminder"
									label={t('ShowOnDashboard', 'Nostetaan etusivulle')}
									onChange={dataChange}
									checked={data?.certificationReminder ?? ''}
								/>
							</Form.Group>

							{data?.certificationReminder && (
								<Form.Group as={Col} xl={4} controlId="certificationReminderDays">
									<Form.Label>{t('NotifyBeforeEnd', 'Ennen voimassaolon umpeutumista')}</Form.Label>
									<Form.Control
										type="number"
										required
										placeholder={t('Days2', 'Päiviä')}
										onChange={dataChange}
										value={data?.certificationReminderDays ?? ''}
									/>
								</Form.Group>
							)}
						</Row>
					)}

					{/*
        <Row className="mb-1">
          <Form.Group as={Col} controlId="trainingPlan">
            <Form.Label></Form.Label>
            <Switch className="mb-1" id="trainingPlan" label={t("EducationPlanDone", "Koulutussuunnitelma laadittu koulutusvähennykselle")} onChange={dataChange} checked={data?.trainingPlan ?? ""} />
          </Form.Group>
        </Row>
        */}

					<Row className="mb-1">
						<Form.Group as={Col} controlId="trainingkeywords">
							<Form.Label>{t('Keywords', 'Avainsanat')}</Form.Label>
							<CreatableSelect
								inputId="trainingkeywords"
								value={data?.tags?.map((tag) => ({ value: tag.codeItemId, label: tag.codeKey }))}
								onChange={handleTags}
								onCreateOption={handleCreateTag}
								options={tagOptions}
								{...creatableSelectTagSettings(t)}
							/>
						</Form.Group>
					</Row>

					<h4>{t('Attachments', 'Liitteet')}</h4>
					<AttachmentView
						section="course"
						entityid={data?.courseId}
						attachments={data?.attachments}
						savedId={
							(actionType === 'POST_COURSE_SUCCEEDED' || actionType === 'POST_COURSE_EDIT_SUCCEEDED') && data?.courseId
						}
					/>
				</Modal.Body>
				<Modal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
					<Button testId="cancel" variant="cancel" onClick={handleClose}>
						{t('Cancel', 'Keskeytä')}
					</Button>
					<Button testId="save" variant="primary" onClick={handleSave} disabled={!data?.personId}>
						{t('Save', 'Tallenna')}
					</Button>
				</Modal.Footer>
			</Modal>
		)
	)
}
